import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditCard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Card, LessonCard } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import { assign, get } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  oldCardStackId: string
  cardStackId: string
  card: Card
  oldCard: Card
}

export const LESSON_CARD_MOVE_TO_NEW_LESSON = {
  name: "LESSON_CARD_MOVE_TO_NEW_LESSON",

  patches(payload: Payload) {
    let oldLessonPatch = Patch.create("card-stack", payload.oldCardStackId).pull(
      "attributes.cards",
      {
        id: payload.oldCard.id,
      }
    ).patch

    // should this be .set? If the array is all the sections including the old one then that makes sense, but if not then shouldn't it be addToSet?
    let newLessonPatch = Patch.create("card-stack", payload.cardStackId).push(
      "attributes.cards",
      payload.card
    ).patch

    // Tell the card's comments that the card moved.
    const commentIds = (payload.card as LessonCard).attributes.commentIds ?? []
    const commentPatches = commentIds.map(
      (commentId) =>
        Patch.create("comment", commentId)
          .set("attributes.primaryObjectId", payload.cardStackId)
          .set("attributes.adverbialObjectId", payload.card.id).patch
    )

    return [oldLessonPatch, newLessonPatch, ...commentPatches]
  },

  undoPatches(payload: Payload) {
    //Is addToSet correct here? I don't think this accounts for position...
    let oldLessonPatch = Patch.create("card-stack", payload.oldCardStackId).addToSet(
      "attributes.cards",
      JSON.parse(JSON.stringify(payload.oldCard))
    ).patch

    let newLessonPatch = Patch.create("card-stack", payload.cardStackId).pull("attributes.cards", {
      id: payload.card.id,
    }).patch

    const commentIds = (payload.card as LessonCard).attributes.commentIds ?? []
    const commentPatches = commentIds.map(
      (commentId) =>
        Patch.create("comment", commentId)
          .set("attributes.primaryObjectId", payload.oldCardStackId)
          .set("attributes.adverbialObjectId", payload.oldCard.id).patch
    )

    return [oldLessonPatch, newLessonPatch, ...commentPatches]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let context = defaultLessonContext(payload, models)
    let cardTitle = get(payload.oldCard, "attributes.title")
    let oldLesson = findFn("card-stack", payload.oldCardStackId)
    let oldLessonTitle = get(oldLesson, "attributes.title")
    let newLessonTitle = get(context, "cardStackTitle")

    let newContext = assign({}, context, {
      moveType: "toNewLesson",
      cardTitle: cardTitle,
      oldLessonTitle: oldLessonTitle,
      oldLessonId: oldLesson.id,
    })
    let newEvent: LessonEditCard = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "cards",

      adverbial_verb: "dragging_dropping",
      adverbial_object_type: "card",
      adverbial_object_specifier: "within_lesson",
      adverbial_object_id: payload.card.id,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    return {
      title: "Moved a Lesson Card",
      titleForAnalytics: "Lesson Card - Move",
      verbForStream: "edited",
      description: `Moved "${cardTitle}" card from ${oldLessonTitle} to ${newLessonTitle}`,
      context: newContext,
      activity: defaultLessonActivity(payload, models, "moved"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_CARD_MOVE_TO_NEW_LESSON
